import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import type { GQLLocale } from 'graphql-ts-types';

import cookieStorage from '../CookieStorage';
import { EventBusEvent } from '../enums/EventBus';
import eventBus from '../event-bus/EventBus';
import { addToWishlist } from '../gql/mutation/wishlist/addToWishlist';
import { removeFromWishlist } from '../gql/mutation/wishlist/removeFromWishlist';
import type {
  AddArticlesToWishlistVariables,
  RemoveFromWishlistVariables,
} from '../gql/mutation/wishlist/types';
import type { WishlistProviderContext } from '../providers/WishlistProvider';
import type { DispatchState } from '../types/React';
import { getReCaptchaChallenge, ReCaptchaAction } from '../utils/grecaptcha';
import { useGQLLocale } from './locale';

interface UseWishListOptions {
  sku: string;
  isWishlisted: boolean;
  setIsWishlisted: DispatchState<boolean>;
  onError: (args: string) => void;
  onSuccess: (args: boolean) => void;
  onFinished?: () => void;
  optimistic?: boolean;
}

interface CallWishlistActionParams {
  isWishlisted: boolean;
  sku: string;
  locale: GQLLocale;
}

const getWishlistActionVariables = async ({
  isWishlisted,
  sku,
  locale,
}: CallWishlistActionParams): Promise<
  AddArticlesToWishlistVariables | RemoveFromWishlistVariables
> => {
  if (!isWishlisted) {
    const challenge = await getReCaptchaChallenge(ReCaptchaAction.AddToWishlist);
    const variables: AddArticlesToWishlistVariables = {
      sku: [sku],
      locale,
      challenge,
    };

    return variables;
  }

  const variables: RemoveFromWishlistVariables = {
    sku,
    locale,
  };

  return variables;
};

export const useWishlist = ({
  sku,
  isWishlisted,
  setIsWishlisted,
  onError,
  onSuccess,
  onFinished,
  optimistic = false,
}: UseWishListOptions) => {
  const locale = useGQLLocale();
  const [add, { loading: addLoading }] = useMutation(addToWishlist);
  const [remove, { loading: removeLoading }] = useMutation(removeFromWishlist);

  const clickHandler = useCallback(
    async event => {
      event.nativeEvent.stopImmediatePropagation();
      const currentIsWishlisted = isWishlisted;
      const variables = await getWishlistActionVariables({
        sku,
        locale,
        isWishlisted: currentIsWishlisted,
      });

      if (optimistic) {
        setIsWishlisted(!currentIsWishlisted);
      }

      const wishlistAction = currentIsWishlisted ? remove : add;

      const { data } = await wishlistAction({
        variables,
        onError: error => {
          onError(error?.graphQLErrors?.[0]?.extensions?.code as string);
          if (optimistic) {
            setIsWishlisted(currentIsWishlisted);
          }
        },
      });

      if (data?.wishlist) {
        const wishlistItemsCount = (data?.wishlist?.articles ?? []).length;
        cookieStorage.setWishlistCount(wishlistItemsCount, { path: '/' });
        eventBus.emit(EventBusEvent.WishlistCounterUpdate, { wishlistItemsCount });
        if (!optimistic) {
          setIsWishlisted(!currentIsWishlisted);
        }
        onSuccess(!currentIsWishlisted);
      }
      onFinished?.();
    },
    [sku, isWishlisted, onError, onSuccess, onFinished, add, remove]
  );

  return { isInProgress: addLoading || removeLoading, clickHandler };
};

export const useWishlistState = (
  sku: string,
  wishlistContextState?: WishlistProviderContext['state']
): [boolean, DispatchState<boolean>] => {
  const [isWishlisted, setIsWishlisted] = useState(
    Boolean(wishlistContextState?.wishlistedItems.has(sku))
  );
  useEffect(() => {
    setIsWishlisted(Boolean(wishlistContextState?.wishlistedItems.has(sku)));
  }, [wishlistContextState?.wishlistedItems, sku]);

  return [isWishlisted, setIsWishlisted];
};
